import {
  type PropertyT,
  type PropertyCreateT,
  type PropertyUpdateT,
} from '@ruminati/types_v2/property'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import { CommonApiV2Instance } from '@/services/common'
import { useOrganisationStore } from '@/stores/organisations'
import { usePropertiesStore } from '@/stores/properties'

import { defaultQueryOptions } from './queryDefaults'

export const queryKeysProperties = {
  all: (organisationId: string | undefined) => ['properties', organisationId] as const,
  byId: (organisationId: string | undefined, propertyId: string | undefined) =>
    ['properties', organisationId, propertyId] as const,
}

export function usePropertiesQuery(options?: { enabled?: MaybeRefOrGetter<boolean> }) {
  const propertiesStore = usePropertiesStore()
  const organisationStore = useOrganisationStore()
  const queryEnabled = computed(() => {
    return (
      (options?.enabled === undefined ? true : toValue(options.enabled)) &&
      !!organisationStore.currentOrgId &&
      organisationStore.currentOrg?.type !== 'vision'
    )
  })

  return useQuery({
    queryKey: computed(() => queryKeysProperties.all(organisationStore.currentOrgId)),
    queryFn: async ({ signal }) => {
      let url: string
      if (organisationStore.currentOrg?.type === 'records') {
        url = `organisations/${organisationStore.currentOrgId}/clients/properties`
      } else if (organisationStore.currentOrg?.type === 'ruminati') {
        url = 'admin/properties'
      } else {
        url = `organisations/${organisationStore.currentOrgId}/properties`
      }

      const properties = await CommonApiV2Instance.get(url, { signal }).json<Array<PropertyT>>()

      propertiesStore.setProperties(properties)
      return properties
    },
    enabled: queryEnabled,
    ...defaultQueryOptions,
  })
}

export function usePropertyQuery(options: {
  propertyId: MaybeRefOrGetter<string>
  enabled?: MaybeRefOrGetter<boolean>
}) {
  const propertiesStore = usePropertiesStore()
  const organisationStore = useOrganisationStore()
  const queryPropertyId = computed(() => toValue(options.propertyId))
  const queryEnabled = computed(() => {
    return (
      (options.enabled === undefined ? true : toValue(options.enabled)) &&
      !!organisationStore.currentOrgId &&
      !!queryPropertyId.value
    )
  })

  return useQuery({
    queryKey: computed(() =>
      queryKeysProperties.byId(organisationStore.currentOrgId, queryPropertyId.value),
    ),
    queryFn: async ({ signal }) => {
      let url = `properties/${queryPropertyId.value}`
      if (organisationStore.currentOrg?.type === 'vision') {
        url = `organisations/${organisationStore.currentOrgId}/linked-farms/received/properties/${queryPropertyId.value}`
      }

      const property = await CommonApiV2Instance.get(url, { signal }).json<PropertyT>()

      propertiesStore.addOrReplaceProperty(property)
      return property
    },
    enabled: queryEnabled,
    ...defaultQueryOptions,
  })
}

export function useCreateProperty() {
  const propertiesStore = usePropertiesStore()
  const organisationStore = useOrganisationStore()

  return useMutation({
    mutationFn: async (details: PropertyCreateT) => {
      // N.B. partners can create properties for other orgs i.e. clients
      const ownerOrg = details.organisationId ?? organisationStore.currentOrgId

      return CommonApiV2Instance.post(`organisations/${ownerOrg}/properties`, {
        json: details,
      }).json<PropertyT>()
    },
    onSuccess: (newProperty: PropertyT) => {
      propertiesStore.addOrReplaceProperty(newProperty)
    },
  })
}

export function usePatchProperty() {
  const propertiesStore = usePropertiesStore()

  return useMutation({
    mutationFn: async (options: { propertyId: string; data: Partial<PropertyUpdateT> }) => {
      return CommonApiV2Instance.patch(`properties/${options.propertyId}`, {
        json: options.data,
      }).json<PropertyT>()
    },
    onSuccess: (updatedProperty: PropertyT) => {
      propertiesStore.addOrReplaceProperty(updatedProperty)
    },
  })
}

export function useDeleteProperty() {
  const propertiesStore = usePropertiesStore()

  return useMutation({
    mutationFn: async (propertyId: string) => {
      await CommonApiV2Instance.delete(`properties/${propertyId}`, {
        headers: {
          'content-type': undefined,
        },
      })

      return propertyId
    },
    onSuccess: (deletedPropertyId: string) => {
      propertiesStore.removeProperty(deletedPropertyId)
    },
  })
}
